import React, { useState, useEffect } from 'react';
import {
  FiPlus,
  FiTrash2,
  FiChevronUp,
  FiChevronDown,
  FiMove,
  FiHelpCircle,
  FiEye,
  FiCheck,
  FiX,
  FiAlertCircle,
  FiLayers
} from 'react-icons/fi';

const YAMLTemplateEditor = ({ value, onChange, onValidationChange }) => {
  const [sections, setSections] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [preservedYamlPrefix, setPreservedYamlPrefix] = useState('');

  // Analysis type options
  const analysisTypes = [
    { value: 'performance_analysis', label: 'Performance Analysis', description: 'Focus on achievements and strengths' },
    { value: 'improvement_recommendations', label: 'Areas for Improvement', description: 'Specific recommendations for development' },
    { value: 'personal_encouragement', label: 'Personal Encouragement', description: 'Motivational and supportive feedback' },
    { value: 'content_coverage', label: 'Content Coverage', description: 'What was covered in the assessment' },
    { value: 'general', label: 'General Comment', description: 'Overall assessment commentary' }
  ];

  // Parse YAML value into sections when component mounts or value changes
  useEffect(() => {
    if (value) {
      try {
        console.log('YAMLTemplateEditor - Parsing YAML value:', value);
        // Simple YAML parser for our specific structure
        const parsedSections = parseYAMLToSections(value);
        setSections(parsedSections);
        setValidationErrors([]);
      } catch (error) {
        console.error('Error parsing YAML:', error);
        setValidationErrors([`Invalid YAML structure: ${error.message}`]);
      }
    } else {
      setSections([]);
    }
  }, [value]);

  // Convert sections back to YAML and notify parent
  useEffect(() => {
    if (sections.length > 0) {
      const yamlString = sectionsToYAML(sections);
      console.log('YAMLTemplateEditor - Converting sections to YAML:', yamlString);
      console.log('YAMLTemplateEditor - Preserved prefix:', preservedYamlPrefix);
      onChange(yamlString);
    }
  }, [sections, onChange, preservedYamlPrefix]);

  // Notify parent about validation state
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validationErrors.length === 0);
    }
  }, [validationErrors, onValidationChange]);

  // Initialize expanded state only when component mounts or section count changes significantly
  useEffect(() => {
    // Only reset if we have no expanded state or the section count changed dramatically
    if (Object.keys(expandedSections).length === 0 || 
        Math.abs(sections.length - Object.keys(expandedSections).length) > 1) {
      const expanded = {};
      sections.forEach((section, index) => {
        // Preserve existing expanded state if index exists, otherwise start collapsed
        expanded[index] = expandedSections[index] || false;
      });
      setExpandedSections(expanded);
    }
  }, [sections.length]);

  const parseYAMLToSections = (yamlString) => {
    const sections = [];
    const lines = yamlString.split('\n');
    let currentSection = null;
    let inCommentSections = false;
    let prefixLines = [];
    let foundCommentSections = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      if (trimmedLine === 'comment_sections:') {
        inCommentSections = true;
        foundCommentSections = true;
        continue;
      }
      
      // Preserve any YAML before comment_sections (like learner_data_map)
      if (!foundCommentSections) {
        prefixLines.push(line);
        continue;
      }
      
      if (!inCommentSections) continue;
      
      // Check for section key (starts with 2 spaces, no colon in value)
      if (trimmedLine.match(/^[a-zA-Z_][a-zA-Z0-9_]*:$/)) {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = {
          key: trimmedLine.replace(':', ''),
          title: '',
          subtitle: '',
          bullet_points: 2,
          analysis_type: 'general',
          include_name: false
        };
        continue;
      }
      
      // Parse section properties
      if (currentSection && line.includes(':')) {
        const [key, ...valueParts] = line.split(':');
        const value = valueParts.join(':').trim().replace(/^["']|["']$/g, '');
        
        switch (key.trim()) {
          case 'title':
            currentSection.title = value;
            break;
          case 'subtitle':
            currentSection.subtitle = value;
            break;
          case 'bullet_points':
            currentSection.bullet_points = parseInt(value) || 2;
            break;
          case 'analysis_type':
            currentSection.analysis_type = value;
            break;
          case 'include_name':
            currentSection.include_name = value === 'true';
            break;
        }
      }
    }
    
    if (currentSection) {
      sections.push(currentSection);
    }
    
    // Store the preserved prefix (like learner_data_map)
    setPreservedYamlPrefix(prefixLines.join('\n'));
    
    return sections;
  };

  const sectionsToYAML = (sections) => {
    if (sections.length === 0) return '';
    
    let yaml = '';
    
    // Include preserved YAML prefix (like learner_data_map) if it exists
    if (preservedYamlPrefix && preservedYamlPrefix.trim()) {
      yaml = preservedYamlPrefix + '\n';
    }
    
    yaml += 'comment_sections:\n';
    
    sections.forEach(section => {
      yaml += `  ${section.key}:\n`;
      yaml += `    title: "${section.title}"\n`;
      if (section.subtitle) {
        yaml += `    subtitle: "${section.subtitle}"\n`;
      }
      if (section.bullet_points !== undefined) {
        yaml += `    bullet_points: ${section.bullet_points}\n`;
      }
      yaml += `    analysis_type: "${section.analysis_type}"\n`;
      if (section.include_name) {
        yaml += `    include_name: true\n`;
      }
    });
    
    return yaml;
  };

  // Pre-built section templates
  const sectionTemplates = [
    {
      key: 'general',
      title: 'General Comment',
      subtitle: 'Overall performance and conduct',
      bullet_points: 1,
      analysis_type: 'personal_encouragement',
      include_name: true
    },
    {
      key: 'strengths',
      title: 'Has Under Control',
      subtitle: 'Areas of strength and competency',
      bullet_points: 2,
      analysis_type: 'performance_analysis',
      include_name: false
    },
    {
      key: 'improvements',
      title: 'Needs Attention',
      subtitle: 'Areas requiring improvement and focus',
      bullet_points: 2,
      analysis_type: 'improvement_recommendations',
      include_name: false
    },
    {
      key: 'performance_overview',
      title: 'Performance Overview',
      subtitle: 'Overall assessment performance',
      bullet_points: 2,
      analysis_type: 'performance_analysis',
      include_name: false
    },
    {
      key: 'content_coverage',
      title: 'Content Coverage',
      subtitle: 'Topics and skills covered',
      bullet_points: 2,
      analysis_type: 'content_coverage',
      include_name: false
    }
  ];

  const addSection = (template = null) => {
    const newSection = template ? {
      ...template,
      key: `${template.key}_${sections.length + 1}`
    } : {
      key: `section_${sections.length + 1}`,
      title: 'New Section',
      subtitle: '',
      bullet_points: 2,
      analysis_type: 'general',
      include_name: false
    };
    setSections([...sections, newSection]);
  };

  const removeSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const updateSection = (index, field, value) => {
    const updatedSections = [...sections];
    updatedSections[index] = { ...updatedSections[index], [field]: value };
    setSections(updatedSections);
  };

  const moveSection = (index, direction) => {
    const newSections = [...sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < sections.length) {
      [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
      setSections(newSections);
    }
  };

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getAnalysisTypeInfo = (type) => {
    return analysisTypes.find(t => t.value === type) || analysisTypes[0];
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Comment Sections</h3>
          <p className="text-sm text-gray-500">Configure the sections that will appear in generated comments</p>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <FiEye className="w-4 h-4 mr-1" />
            {showPreview ? 'Hide' : 'Show'} Preview
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowTemplateMenu(!showTemplateMenu)}
              className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <FiLayers className="w-4 h-4 mr-1" />
              Quick Add
            </button>
            {showTemplateMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                <div className="py-1">
                  {sectionTemplates.map((template) => (
                    <button
                      key={template.key}
                      type="button"
                      onClick={() => {
                        addSection(template);
                        setShowTemplateMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                    >
                      <div className="font-medium">{template.title}</div>
                      <div className="text-gray-500 text-xs">{template.subtitle}</div>
                    </button>
                  ))}
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    type="button"
                    onClick={() => {
                      addSection();
                      setShowTemplateMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    <div className="font-medium">Blank Section</div>
                    <div className="text-gray-500 text-xs">Start with an empty section</div>
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={addSection}
            className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <FiPlus className="w-4 h-4 mr-1" />
            Add Section
          </button>
        </div>
      </div>

      {validationErrors.length > 0 && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center">
            <FiAlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <h4 className="text-sm font-medium text-red-800">Validation Errors</h4>
          </div>
          <ul className="mt-2 text-sm text-red-700">
            {validationErrors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      {sections.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
          <FiPlus className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">No sections configured. Click "Add Section" to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sections.map((section, index) => (
            <div key={index} className="border border-gray-200 rounded-lg">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => toggleSection(index)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {expandedSections[index] ? (
                        <FiChevronUp className="w-4 h-4" />
                      ) : (
                        <FiChevronDown className="w-4 h-4" />
                      )}
                    </button>
                    <h4 className="font-medium text-gray-900">
                      {section.title || section.key}
                    </h4>
                    <span className="text-sm text-gray-500">
                      ({getAnalysisTypeInfo(section.analysis_type).label})
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => moveSection(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <FiChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveSection(index, 'down')}
                      disabled={index === sections.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <FiChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeSection(index)}
                      className="p-1 text-red-400 hover:text-red-600"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              {expandedSections[index] && (
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Section Key
                        <span className="text-gray-400 text-xs ml-1">(Internal identifier)</span>
                      </label>
                      <input
                        type="text"
                        value={section.key}
                        onChange={(e) => updateSection(index, 'key', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="section_key"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Unique identifier for this section (used internally)
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Title
                        <span className="text-gray-400 text-xs ml-1">(Appears in reports)</span>
                      </label>
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => updateSection(index, 'title', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Section Title"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Title that will appear in the generated report
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subtitle (optional)
                      <span className="text-gray-400 text-xs ml-1">(Additional context)</span>
                    </label>
                    <input
                      type="text"
                      value={section.subtitle}
                      onChange={(e) => updateSection(index, 'subtitle', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional subtitle"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Additional context that appears under the title
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Analysis Type
                      </label>
                      <select
                        value={section.analysis_type}
                        onChange={(e) => updateSection(index, 'analysis_type', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {analysisTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        {getAnalysisTypeInfo(section.analysis_type).description}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bullet Points
                        <span className="text-gray-400 text-xs ml-1">(Comment length)</span>
                      </label>
                      <select
                        value={section.bullet_points}
                        onChange={(e) => updateSection(index, 'bullet_points', parseInt(e.target.value))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {[1, 2, 3, 4, 5].map(num => (
                          <option key={num} value={num}>
                            {num} bullet point{num > 1 ? 's' : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Number of bullet points to generate for this section
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id={`include_name_${index}`}
                      checked={section.include_name}
                      onChange={(e) => updateSection(index, 'include_name', e.target.checked)}
                      className="mr-2 mt-1"
                    />
                    <div>
                      <label htmlFor={`include_name_${index}`} className="text-sm text-gray-700">
                        Include student's name in this section
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        When checked, the student's first name will be included in the generated comment
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showPreview && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">Generated YAML Preview</h4>
          <pre className="text-sm text-gray-700 bg-white p-3 rounded border font-mono whitespace-pre-wrap">
            {sectionsToYAML(sections)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default YAMLTemplateEditor;